/**
 * <TutorHourChip> — une heure de la semaine du tuteur, dans l'un de ses quatre états (molecule).
 *
 * Le seul contrôle de « Ma dispo » (legacy `MaDispoEditor` → `HourChip`). La face dit ce que
 * l'heure EST, jamais la fraîcheur de la semaine — un seul tampon, porté par le bandeau :
 *  - `open` — la pilule de sélection non cochée : le tuteur peut la proposer ;
 *  - `taken` — la pilule cochée, à l'aplat choisi de l'univers, avec la coche (le legacy la mettait
 *    en vert, la couleur de la progression dans le livre : un créneau choisi n'est pas un progrès,
 *    même lecture que `<TimeSlotGrid>`) ;
 *  - `live` — l'heure porte un live : à plat sur l'encart, cernée de la couleur de l'univers, la
 *    caméra devant le libellé. Elle ne se retire pas d'ici : le toucher ouvre sa boîte ;
 *  - `freed` — libérée : barrée, à l'encre secondaire, la croix devant. Inerte, sauf une heure
 *    marquée dans le brouillon de « Libérer des créneaux » (`releasable`), qu'un second toucher rend.
 *
 * Une face de poussoir (`<PushButton>`, taille `sm`), comme la pilule de `<ChoiceChip>` : la même
 * hauteur dans les quatre états, donc une rangée ne saute pas sous le doigt.
 */
import React from "react"

import { PushButton, type PushFace } from "../../atoms/Buttons/Button"
import { type IconName } from "../../atoms/icons/Icon"
import { inkOnFill, reliefOnFill, textInkOnFill } from "../../theme/tokens/colors"
import { type UITutorHour, type UITutorHourState } from "../../../ports/primary/ui/types/tutorDispoOffres"

const CHOSEN = "role-selected"

const FACES: Record<UITutorHourState, { face: PushFace; icon?: IconName }> = {
    open: { face: { fill: "surface-raised", border: "border", relief: "border", ink: textInkOnFill(CHOSEN) } },
    taken: {
        face: { fill: CHOSEN, border: null, relief: reliefOnFill(CHOSEN), ink: inkOnFill(CHOSEN), hoverVeil: true },
        icon: "check",
    },
    live: { face: { fill: "surface-subtle", border: "role", relief: null, ink: "role-strong" }, icon: "video" },
    freed: {
        face: { fill: "surface-subtle", border: "border", relief: null, ink: "text-muted", strike: true },
        icon: "times",
    },
}

export type TutorHourChipProps = {
    hour: UITutorHour
    onPress?: (hour: UITutorHour) => void
    /** La pilule remplit sa colonne — la grille en colonnes, au large. */
    fullWidth?: boolean
    testID?: string
}

export function TutorHourChip(props: TutorHourChipProps) {
    const { hour, onPress, fullWidth = false, testID } = props
    const { face, icon } = FACES[hour.state]
    const toggles = hour.state === "open" || hour.state === "taken"
    const inert = hour.state === "freed" && hour.releasable !== true

    return (
        <PushButton
            testID={testID}
            label={hour.label}
            size="sm"
            face={face}
            iconName={icon}
            accessibilityRole={toggles ? "radio" : "button"}
            selected={toggles ? hour.state === "taken" : undefined}
            accessibilityLabel={hour.accessibilityLabel}
            onPress={() => onPress?.(hour)}
            disabled={inert}
            fullWidth={fullWidth}
        />
    )
}
